import { useEffect, useLayoutEffect, useRef, type FocusEvent, type PointerEvent } from 'react';
import { AnimatePresence, animate, motion, useMotionValue, usePresence, useReducedMotion } from 'motion/react';
import { createStore, useStore } from '../../lib/store';
import { Icon, type CupertinoIconName } from '../icons/Icon';
import { curves, durations } from '../theme/motion';
import { createHostRegistry } from './hostRegistry';
import { IconButton } from './IconButton';
import { settleDurationMs } from './sheetSnap';
import { VelocityTracker } from './velocity';
import styles from './Toast.module.css';

export type ToastTone = 'info' | 'success' | 'error' | 'warning';

/** A text button in the toast (e.g. "Undo"); tapping it dismisses the toast and runs `onClick`. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  tone?: ToastTone;
  /**
   * Auto-dismiss delay in ms. Default: 2600 (3200 for errors), longer for long
   * messages (reading time), and at least 5000 with an action.
   */
  duration?: number;
  action?: ToastAction;
}

interface ToastEntry {
  id: number;
  message: string;
  tone: ToastTone;
  duration: number;
  action: ToastAction | null;
}

/** Messages up to this length read within the base duration. */
const READING_FREE_CHARS = 60;
/** Extra time per character beyond that (about 25 characters a second). */
const READING_MS_PER_CHAR = 40;
const MAX_AUTO_DURATION_MS = 10_000;
/** A toast with an action stays long enough to reach it. */
export const TOAST_ACTION_MIN_MS = 5000;

/** Default auto-dismiss delay: the tone's base, plus reading time for long text, at least 5 s with an action. */
export function toastDuration(message: string, tone: ToastTone, hasAction = false): number {
  const base = tone === 'error' ? durations.toastAutoError : durations.toastAuto;
  const extra = Math.max(0, message.length - READING_FREE_CHARS) * READING_MS_PER_CHAR;
  const reading = Math.min(Math.max(base, MAX_AUTO_DURATION_MS), base + extra);
  return hasAction ? Math.max(reading, TOAST_ACTION_MIN_MS) : reading;
}

const ICONS: Record<ToastTone, CupertinoIconName> = {
  info: 'bell_fill',
  success: 'checkmark_circle_fill',
  error: 'exclamationmark_triangle_fill',
  warning: 'exclamationmark_triangle_fill',
};

/** Movement before a press becomes a swipe (the toast then follows the finger). */
const DRAG_SLOP = 8;

type TimerHold = 'hover' | 'focus' | 'drag';

const toastStore = createStore<ToastEntry | null>(null);
let sequence = 0;
const registry = createHostRegistry('toast', () => ToastHost);

function show(message: string, { tone = 'info', duration, action }: ToastOptions = {}): void {
  registry.ensureMounted();
  toastStore.set({ id: ++sequence, message, tone, duration: duration ?? toastDuration(message, tone, !!action), action: action ?? null });
}

/** The tone helpers take a duration (as before) or the other options. */
type ToneOptions = number | Omit<ToastOptions, 'tone'>;
const toneOptions = (tone: ToastTone, options?: ToneOptions): ToastOptions =>
  typeof options === 'number' ? { tone, duration: options } : { ...options, tone };

/**
 * AppNotification: one top toast at a time; a new toast replaces the current
 * one immediately (spec 01 §10.3), the old one sliding out in the same place.
 */
export const toast = Object.assign(show, {
  info: (message: string, options?: ToneOptions) => show(message, toneOptions('info', options)),
  success: (message: string, options?: ToneOptions) => show(message, toneOptions('success', options)),
  error: (message: string, options?: ToneOptions) => show(message, toneOptions('error', options)),
  warning: (message: string, options?: ToneOptions) => show(message, toneOptions('warning', options)),
});

/** Removes the current toast without animation. */
export function dismissToast(): void {
  instantExitId = toastStore.get()?.id ?? null;
  toastStore.set(null);
}

/** Upward release speed (px/s) that flings the toast away regardless of distance. */
const FLING_VELOCITY = 500;
/** Fraction of its height the toast must be pulled up to dismiss on release. */
const DISMISS_FRACTION = 0.3;
/** Snap-back after a drag that did not dismiss: critically damped, no bounce. */
const SNAP_BACK = { type: 'spring', stiffness: 520, damping: 46, mass: 1 } as const;

/** Toast removed by `dismissToast()`: it leaves without the exit animation. */
let instantExitId: number | null = null;

/** Pulling towards the screen centre: rubber-banded, approaching ~a third of the height. */
function rubberBand(offset: number, height: number): number {
  const limit = height * 0.35;
  return limit * (1 - 1 / (offset / limit + 1));
}

function ToastView({ entry }: { entry: ToastEntry }) {
  const [isPresent, safeToRemove] = usePresence();
  const reduceMotion = useReducedMotion() ?? false;
  const opacity = useMotionValue(0);
  // Pixels; the enter/exit travel is 45 % of the toast's height, as Flutter's slide.
  const y = useMotionValue(0);
  const drag = useRef<{ startY: number; dragging: boolean; tracker: VelocityTracker; pointerId: number } | null>(null);
  const suppressClick = useRef(false);
  /** Release velocity (px/s, + down) of a drag that dismissed the toast, carried into the exit. */
  const releaseVelocity = useRef<number | null>(null);
  /** The running enter slide or snap-back on `y`; a new swipe takes over from it. */
  const settle = useRef<ReturnType<typeof animate> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  /** Auto-dismiss timer; paused while hovered, focused or dragged (WCAG 2.2.1), resumed with the time left. */
  const timer = useRef<{ handle: ReturnType<typeof setTimeout> | null; endsAt: number; remaining: number; holds: Set<TimerHold> }>({
    handle: null,
    endsAt: 0,
    remaining: entry.duration,
    holds: new Set(),
  });

  const height = () => rootRef.current?.offsetHeight || 64;

  /** Asks the host to remove this toast; the exit plays while it unmounts. */
  const dismiss = () => {
    if (toastStore.get()?.id === entry.id) toastStore.set(null);
  };

  const runTimer = () => {
    const t = timer.current;
    if (t.handle != null || t.holds.size > 0 || !isPresent) return;
    t.endsAt = Date.now() + t.remaining;
    t.handle = setTimeout(dismiss, t.remaining);
  };
  const hold = (reason: TimerHold) => {
    const t = timer.current;
    t.holds.add(reason);
    if (t.handle == null) return;
    clearTimeout(t.handle);
    t.handle = null;
    t.remaining = Math.max(0, t.endsAt - Date.now());
  };
  const release = (reason: TimerHold) => {
    timer.current.holds.delete(reason);
    runTimer();
  };

  useLayoutEffect(() => {
    const seconds = reduceMotion ? 0 : durations.toastIn / 1000;
    if (!reduceMotion) y.set(-0.45 * height());
    const a = animate(opacity, 1, { duration: seconds, ease: curves.easeOutCubic });
    settle.current = animate(y, 0, { duration: seconds, ease: curves.easeOutBack });
    runTimer();
    const t = timer.current;
    return () => {
      a.stop();
      settle.current?.stop();
      if (t.handle != null) clearTimeout(t.handle);
      t.handle = null;
    };
  }, []);

  // Exit (replaced by a new toast, timed out, tapped or swiped away). The old
  // toast fades out above the new one instead of vanishing (polish pass).
  useEffect(() => {
    if (isPresent) return;
    const t = timer.current;
    if (t.handle != null) clearTimeout(t.handle);
    t.handle = null;
    settle.current?.stop();
    if (reduceMotion || instantExitId === entry.id) {
      safeToRemove?.();
      return;
    }
    const velocity = releaseVelocity.current;
    const h = height();
    const from = y.get();
    // Swiped away: keep travelling at the finger's speed until it is off the top.
    // Otherwise Flutter's exit: up 45 % of its height while fading, ease-in.
    const swiped = velocity !== null;
    const to = swiped ? -(h + (rootRef.current?.getBoundingClientRect().top ?? 0) + 8) : Math.min(from, 0) - 0.45 * h;
    const duration = swiped ? settleDurationMs(to - from, velocity, durations.swipeMin, durations.toastOut) / 1000 : durations.toastOut / 1000;
    const ease = swiped ? curves.easeOutCubic : curves.easeInCubic;
    const a = animate(y, to, { duration, ease });
    const b = animate(opacity, 0, { duration, ease, onComplete: () => safeToRemove?.() });
    return () => {
      a.stop();
      b.stop();
    };
  }, [isPresent]);

  // Swipe: the toast follows the finger up (towards its edge) and rubber-bands
  // down; released past 30 % of its height or flung up it leaves, otherwise it
  // springs back (polish pass; was "any 18 px movement in any direction").
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!isPresent || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const tracker = new VelocityTracker();
    tracker.add(performance.now(), event.clientY);
    drag.current = { startY: event.clientY - y.get(), dragging: false, tracker, pointerId: event.pointerId };
    suppressClick.current = false;
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== event.pointerId) return;
    const offset = event.clientY - d.startY;
    d.tracker.add(performance.now(), event.clientY);
    if (!d.dragging) {
      if (Math.abs(offset) < DRAG_SLOP) return;
      d.dragging = true;
      suppressClick.current = true;
      settle.current?.stop();
      hold('drag');
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    y.set(offset <= 0 ? offset : rubberBand(offset, height()));
  };
  const onPointerEnd = (event: PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const d = drag.current;
    if (!d || d.pointerId !== event.pointerId) return;
    drag.current = null;
    if (!d.dragging) return;
    const velocity = cancelled ? 0 : d.tracker.velocity(performance.now());
    if (!cancelled && (y.get() < -DISMISS_FRACTION * height() || velocity < -FLING_VELOCITY)) {
      releaseVelocity.current = Math.min(velocity, 0);
      dismiss();
      return;
    }
    settle.current = animate(y, 0, reduceMotion ? { duration: 0 } : { ...SNAP_BACK, velocity });
    release('drag');
  };
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!rootRef.current?.contains(event.relatedTarget as Node | null)) release('focus');
  };
  const action = entry.action;

  return (
    <motion.div
      ref={rootRef}
      className={styles.toast}
      data-tone={entry.tone}
      data-has-action={action ? '' : undefined}
      aria-hidden={isPresent ? undefined : true}
      style={{ opacity, y, pointerEvents: isPresent ? undefined : 'none' }}
      onClick={() => {
        if (suppressClick.current) suppressClick.current = false;
        else dismiss();
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => onPointerEnd(event, false)}
      onPointerCancel={(event) => onPointerEnd(event, true)}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') hold('hover');
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') release('hover');
      }}
      onFocus={() => hold('focus')}
      onBlur={onBlur}
    >
      <div className={styles.surface}>
        <span className={styles.iconTile} aria-hidden>
          <Icon name={ICONS[entry.tone]} size={18} />
        </span>
        <p className={styles.message}>{entry.message}</p>
        {action && (
          <button
            type="button"
            className={styles.action}
            onClick={(event) => {
              event.stopPropagation();
              dismiss();
              action.onClick();
            }}
          >
            {action.label}
          </button>
        )}
        <IconButton
          icon="xmark"
          iconSize={18}
          label="Dismiss"
          tooltip={false}
          size={40}
          visualSize={32}
          className={styles.close}
          onClick={(event) => {
            event.stopPropagation();
            dismiss();
          }}
        />
      </div>
    </motion.div>
  );
}

/**
 * Screen-reader announcements. The live regions stay mounted so they exist
 * before the text arrives (regions inserted already filled are often not read);
 * each toast inserts a new keyed node, so repeating a message re-announces it.
 */
function ToastAnnouncer({ entry }: { entry: ToastEntry | null }) {
  const assertive = entry?.tone === 'error';
  return (
    <>
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {entry && !assertive && <span key={entry.id}>{entry.message}</span>}
      </div>
      <div className="visually-hidden" role="alert" aria-live="assertive" aria-atomic="true">
        {entry && assertive && <span key={entry.id}>{entry.message}</span>}
      </div>
    </>
  );
}

/** Renders the top toast. Mount once in the app shell. */
export function ToastHost() {
  const primary = registry.usePrimaryHost();
  const entry = useStore(toastStore);
  if (!primary) return null;
  return (
    <>
      <ToastAnnouncer entry={entry} />
      <div className={styles.host}>
        <AnimatePresence>{entry && <ToastView key={entry.id} entry={entry} />}</AnimatePresence>
      </div>
    </>
  );
}
